<template>
    <b-container fluid class="overview">
        <h1 v-if="newsType === NEWSITEMS_TYPES.NEWSITEMS" class="text-left">
            {{ $t('menuItemNewsItems') }}
        </h1>
        <h1 v-if="newsType === NEWSITEMS_TYPES.MSGFEEDS" class="text-left">
            {{ $t('menuItemMessagesFeed') }}
        </h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchNewsItems"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <b-link class="px-1 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilter') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="fields"
                sort-icon-left
            >
            </b-table>
        </div>
        <!-- No content for overview -->
        <div
            v-if="(!this.records || this.records.length === 0) && !loading"
            class="col-md-12"
        >
            <span v-if="newsType === NEWSITEMS_TYPES.NEWSITEMS">{{
                $t('newsItemNonePresent')
            }}</span>
            <span v-if="newsType === NEWSITEMS_TYPES.MSGFEEDS">{{
                $t('msgFeed.nonePresent')
            }}</span>
        </div>
        <router-links-footer
            v-if="userInfo.isSuper && newsType === NEWSITEMS_TYPES.NEWSITEMS"
            :routerLinks="[{ name: 'newsItemDetailNew' }]"
        />
        <router-links-footer
            v-if="newsType === NEWSITEMS_TYPES.MSGFEEDS"
            :routerLinks="[{ name: 'msgFeedDetailNew' }]"
        />
    </b-container>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { PRIORITY, NEWSITEMS_TYPES } from '../../constants';
    import { orderBy } from 'lodash';
    import SearchBox from '../../components/SearchBox.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';
    import { mapState } from 'vuex';

    export default {
        name: 'NewsItemsOverview',

        mixins: [refcodeMixin],

        props: {
            newsType: {
                type: String,
                required: false,
                default: NEWSITEMS_TYPES.NEWSITEMS
            }
        },

        components: {
            'search-box': SearchBox,
            'router-links-footer': RouterLinksFooter
        },

        data: function () {
            return {
                fields: [
                    {
                        key: 'date',
                        label: this.$t('columnNameDate'),
                        sortable: true,
                        stickyColumn: true,
                        thStyle: {
                            border: 0,
                            width: '200px'
                        }
                    },
                    {
                        key: 'prioDescription',
                        label: this.$t('columnNamePriority'),
                        sortable: true,
                        thStyle: {
                            border: 0,
                            width: '200px'
                        }
                    },
                    {
                        key: 'title',
                        label: this.$t('columnNameTitle'),
                        sortable: true,
                        thStyle: { border: 0 }
                    }
                ],
                priorityTypes: null,
                records: [],
                searchText: '',
                NEWSITEMS_TYPES: NEWSITEMS_TYPES,
                // loading: during load no test-message shown that there are no messages yet
                loading: true
            };
        },

        computed: {
            ...mapState(['userInfo']),

            dataLoaded() {
                return this.records.length > 0;
            },

            filteredRecords() {
                let records = this.records;
                // Filter records
                if (this.searchText && this.searchText.length > 0) {
                    const tokens = this.searchText.toLowerCase();
                    records = this.records.filter((record) => {
                        return Object.keys(record).some((key) =>
                            record[key].toString().toLowerCase().includes(tokens)
                        );
                    });
                }
                return records;
            }
        },

        async created() {
            nprogress.start();
            this.loading = true;
            // Get all the data from the backend
            let result = null;
            if (this.newsType === NEWSITEMS_TYPES.NEWSITEMS) {
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetNewsItemOverview'
                });
            } else {
                // MsgFeeds has extra column publish
                this.fields.push({
                    key: 'showInMobile',
                    label: this.$t('msgFeed.publishToMobile'),
                    sortable: true,
                    thStyle: { border: 0 },
                    formatter: (value) => (value ? this.$t('btnTitleYes') : '')
                });

                result = await this.$store.dispatch('useApiData', {
                    name: 'GetMsgFeedOverview'
                });
            }

            let records = result?.data;
            records = orderBy(records, 'date', 'desc');

            // Retrieve Priority types reference code list
            this.getRefCodes(PRIORITY.KEY).then((refCodes) => {
                this.priorityTypes = refCodes.data;

                const self = this;
                records.forEach(function (record) {
                    const dateTimeParts = record.date.split('T');
                    record.date = `${dateTimeParts[0]}`;
                    record.prioDescription = self.$_getPriorityDescription(
                        record.priority
                    );
                });

                self.records = records;
            });
            this.loading = false;
            nprogress.done();
        },

        methods: {
            $_onInput(searchText) {
                this.searchText = searchText;
            },

            $_onDetails(record) {
                // Set searchTerm as route parameter, and redirect to details
                if (this.newsType === NEWSITEMS_TYPES.NEWSITEMS) {
                    this.$router.push({
                        path: `/dashboard/newsItem/detail/${record.id}/${this.searchText}`
                    });
                } else {
                    // msgFeeds
                    this.$router.push({
                        path: `/dashboard/msgfeed/detail/${record.id}/${this.searchText}`
                    });
                }
            },

            $_rowClass(item) {
                return item.priority === PRIORITY.HIGH
                    ? 'tablerow priority-high'
                    : 'tablerow';
            },

            // Get description from PRIORITY code
            $_getPriorityDescription: function (code) {
                let description;
                const type = this.priorityTypes.find((s) => s.code === code);
                if (type) {
                    description = type.description;
                }
                return description;
            },

            $_clearFilters() {
                this.$_onInput('');
            }
        }
    };
</script>
